import * as routes from 'api';
import { pollWorkerStatus } from 'api/workerStatus';

import * as meteringActions from 'actions/metering';

import { buildGetDrawerOpen } from 'selectors/drawers';
import { getCurrentLocationPartnerName } from 'selectors/session';

import {
  EMPTY_SCHEDULE_DRAWER_KEY,
  PRINT_DRAWER_KEY,
  PUBLISH_DRAWER_KEY,
  TEMPLATES_DRAWER_KEY,
} from 'features/drawers/keys';
import { trackPublishDrawerUxEvents } from 'features/scheduleBuilder/tracking';

import { df } from 'util/dateTime';
import * as flashNotice from 'util/flashNotice';
import { hideGlobalLoader, showGlobalLoader } from 'util/globalLoader';
import { toI18n } from 'util/i18n';
import { createAsyncPutAction, withAlerts } from 'util/redux';
import { EVENT_ACTIONS } from 'util/tracking_constants';
import { showUpgradeModal } from 'util/upgrades';

export const actionTypes = {
  UPDATE_CURRENT_TEMPLATE: 'SCHEDULE_BUILDER/UPDATE_CURRENT_TEMPLATE',
  COPY_WEEK_REQUEST: 'SCHEDULE_BUILDER/COPY_WEEK_REQUEST',
  COPY_WEEK_SUCCESS: 'SCHEDULE_BUILDER/COPY_WEEK_SUCCESS',
  COPY_WEEK_FAILURE: 'SCHEDULE_BUILDER/COPY_WEEK_FAILURE',
  TOGGLE_PUBLISH_DRAWER: 'SCHEDULE_BUILDER/TOGGLE_PUBLISH_DRAWER',
  TOGGLE_EMPTY_SCHEDULE_DRAWER: 'SCHEDULE_BUILDER/TOGGLE_EMPTY_SCHEDULE_DRAWER',
  TOGGLE_TEMPLATES_DRAWER: 'SCHEDULE_BUILDER/TOGGLE_TEMPLATES_DRAWER',
  TOGGLE_PRINT_DRAWER: 'SCHEDULE_BUILDER/TOGGLE_PRINT_DRAWER',
  TOGGLE_SETTINGS_DRAWER: 'SCHEDULE_BUILDER/TOGGLE_SETTINGS_DRAWER',
  PUBLISH_SCHEDULE_REQUEST: 'SCHEDULE_BUILDER/PUBLISH_SCHEDULE_REQUEST',
  PUBLISH_SCHEDULE_SUCCESS: 'SCHEDULE_BUILDER/PUBLISH_SCHEDULE_SUCCESS',
  PUBLISH_SCHEDULE_FAILURE: 'SCHEDULE_BUILDER/PUBLISH_SCHEDULE_FAILURE',
  FETCH_TEMPLATES_REQUEST: 'SCHEDULE_BUILDER/FETCH_TEMPLATES_REQUEST',
  FETCH_TEMPLATES_SUCCESS: 'SCHEDULE_BUILDER/FETCH_TEMPLATES_SUCCESS',
  FETCH_TEMPLATES_FAILURE: 'SCHEDULE_BUILDER/FETCH_TEMPLATES_FAILURE',
  CREATE_FROM_TEMPLATE_REQUEST: 'SCHEDULE_BUILDER/CREATE_FROM_TEMPLATE_REQUEST',
  CREATE_FROM_TEMPLATE_SUCCESS: 'SCHEDULE_BUILDER/CREATE_FROM_TEMPLATE_SUCCESS',
  CREATE_FROM_TEMPLATE_FAILURE: 'SCHEDULE_BUILDER/CREATE_FROM_TEMPLATE_FAILURE',
  DELETE_TEMPLATE_REQUEST: 'SCHEDULE_BUILDER/DELETE_TEMPLATE_REQUEST',
  DELETE_TEMPLATE_SUCCESS: 'SCHEDULE_BUILDER/DELETE_TEMPLATE_SUCCESS',
  DELETE_TEMPLATE_FAILURE: 'SCHEDULE_BUILDER/DELETE_TEMPLATE_FAILURE',
  RENAME_TEMPLATE_REQUEST: 'SCHEDULE_BUILDER/RENAME_TEMPLATE_REQUEST',
  RENAME_TEMPLATE_SUCCESS: 'SCHEDULE_BUILDER/RENAME_TEMPLATE_SUCCESS',
  RENAME_TEMPLATE_FAILURE: 'SCHEDULE_BUILDER/RENAME_TEMPLATE_FAILURE',
  SHOW_AFTER_PUBLISH_DRAWER: 'SCHEDULE_BUILDER/SHOW_AFTER_PUBLISH_DRAWER',
  SHOW_ONBOARDING_AFTER_PUBLISH_DRAWER:
    'SCHEDULE_BUILDER/SHOW_ONBOARDING_AFTER_PUBLISH_DRAWER',
  SET_SYNC_ERRORS: 'SCHEDULE_BUILDER/SET_SYNC_ERRORS',
  TOGGLE_SYNC_SHIFTS_DRAWER: 'SCHEDULE_BUILDER/TOGGLE_SYNC_SHIFTS_DRAWER',
};

export const showAfterPublishDrawer = () => ({
  type: actionTypes.SHOW_AFTER_PUBLISH_DRAWER,
});

export const checkPublishJobDoneLegacy = (dispatch, getState, jobId) => {
  const partnerName = getCurrentLocationPartnerName(getState());

  pollWorkerStatus(jobId)
    .then(resp => {
      const errors = JSON.parse(resp.errors);

      if (errors.length) {
        flashNotice.show(
          'error',
          toI18n('alerts.shifts_sync_failure_flash', {
            props: { partner: partnerName },
          })
        );
      } else {
        flashNotice.show(
          'notice',
          toI18n('alerts.shifts_sync_success_flash', {
            props: { partner: partnerName },
          })
        );
      }
      window.Backbone.Mediator.pub('schedule:sync-errors:add', errors);
    })
    .catch(() => {
      flashNotice.show(
        'error',
        toI18n('alerts.shifts_sync_failure_flash', {
          props: { partner: partnerName },
        })
      );
    });
};

export const togglePublishDrawer = (show, data, published = false) => ({
  type: actionTypes.TOGGLE_PUBLISH_DRAWER,
  payload: {
    data,
    published,
    drawerKey: PUBLISH_DRAWER_KEY,
    drawerOpen: show,
  },
});

export const publishScheduleLegacy =
  ({ startDate, endDate, departmentId, sendNotifications, afterSuccess }) =>
  (dispatch, getState) => {
    showGlobalLoader();

    return dispatch(
      withAlerts(
        createAsyncPutAction(
          routes.publishScheduleRoute(),
          [
            { type: actionTypes.PUBLISH_SCHEDULE_REQUEST },
            { type: actionTypes.PUBLISH_SCHEDULE_SUCCESS },
            { type: actionTypes.PUBLISH_SCHEDULE_FAILURE },
          ],
          {
            body: {
              department_id: departmentId,
              send_notifications: sendNotifications,
              start_date: startDate.format(df('parsable_date')),
              end_date: endDate.format(df('parsable_date')),
            },
          }
        ),
        {
          noErrorFlash: true,
          onError: response => {
            const { access_error, feature, min_tier } =
              response.payload.response;
            hideGlobalLoader();
            if (access_error) {
              showUpgradeModal({ minTier: min_tier, feature });
            }
          },
          onSuccess: async response => {
            trackPublishDrawerUxEvents({
              eventAction: EVENT_ACTIONS.SCHEDULE_PUBLISHED,
            });

            window.Backbone.Mediator.pub(
              'schedule:after_publish',
              departmentId,
              response.payload
            );

            const jobId = response.payload.job_id;
            if (jobId) {
              checkPublishJobDoneLegacy(dispatch, getState, jobId);
            }

            dispatch(showAfterPublishDrawer());

            dispatch(meteringActions.fetchMeteringShiftNotesForDateCount());

            hideGlobalLoader();

            if (afterSuccess) {
              afterSuccess();
            }
          },
        }
      )
    );
  };

export const toggleEmptyScheduleDrawer = ({ visible, ...params }) => ({
  type: actionTypes.TOGGLE_EMPTY_SCHEDULE_DRAWER,
  payload: {
    ...params,
    drawerKey: EMPTY_SCHEDULE_DRAWER_KEY,
    drawerOpen: visible,
  },
});

export const updateCurrentTemplate = id => ({
  type: actionTypes.UPDATE_CURRENT_TEMPLATE,
  payload: { id },
});

export const togglePrintDrawer = (show, viewType, startDate, endDate) => ({
  type: actionTypes.TOGGLE_PRINT_DRAWER,
  payload: {
    viewType,
    startDate,
    endDate,
    drawerKey: PRINT_DRAWER_KEY,
    drawerOpen: show,
  },
});

export const toggleTemplatesDrawer =
  (forceShow, currentTemplateId) => (dispatch, getState) => {
    const show =
      forceShow === undefined
        ? !buildGetDrawerOpen(TEMPLATES_DRAWER_KEY)(getState())
        : forceShow;

    dispatch({
      type: actionTypes.TOGGLE_TEMPLATES_DRAWER,
      payload: {
        drawerKey: TEMPLATES_DRAWER_KEY,
        drawerOpen: !!show,
        currentTemplateId,
      },
    });
  };
